<template>
  <div>
    <!-- Barre de titre : recherche à gauche, action à droite -->
    <b-card class="card-custom mt-3 mb-3 card-titre sticky-top" no-body style="z-index: 25;">
      <template #header>
        <div class="card-title">
          <h3 class="card-label p-3 bg-primary text-white rounded">Bibliothèque</h3>
        </div>
        <div class="card-title flex-grow-1 justify-content-end">
          <b-input-group class="biblio-search mr-2">
            <b-input-group-prepend is-text>
              <i class="fas fa-search"></i>
            </b-input-group-prepend>
            <b-form-input v-model="search" placeholder="Rechercher un titre, un contenu..." trim></b-form-input>
            <b-input-group-append v-if="search !== ''">
              <b-button variant="light" @click="search = ''">
                <i class="fas fa-times"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <b-button v-if="$api.acl(36)" v-b-tooltip.hover title="Ajouter à la bibliothèque" variant="success" class="btn-icon" size="md" @click="nouveau()">
            <i class="fas" :class="load_new ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
          </b-button>
        </div>
      </template>
    </b-card>

    <!-- Filtres par type : seuls les types réellement présents sont proposés -->
    <div class="mb-3 biblio-filtres">
      <b-button
        v-for="f in filtres"
        :key="f.value === null ? 'tous' : f.value"
        size="sm"
        class="mr-2 mb-2 rounded-pill px-3"
        :variant="filtre_type === f.value ? 'primary' : 'light'"
        @click="filtre_type = f.value">
        <i class="fas mr-1" :class="f.value === null ? 'fa-layer-group' : icone(f.value)"></i>
        {{ f.titre }}
        <b-badge class="ml-1" :variant="filtre_type === f.value ? 'light' : 'primary'">{{ f.total }}</b-badge>
      </b-button>
    </div>

    <!-- Chargement -->
    <div v-if="load_liste" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
    </div>

    <!-- Aucun résultat -->
    <b-card v-else-if="liste.length === 0" class="card-search text-center py-5">
      <i class="fas fa-3x d-block mb-3 text-muted" :class="items.length === 0 ? 'fa-inbox' : 'fa-search'"></i>
      <span class="h6 text-muted" v-if="items.length === 0">La bibliothèque est vide pour le moment.</span>
      <span class="h6 text-muted" v-else>Aucun élément ne correspond à votre recherche.</span>
    </b-card>

    <!-- Grille -->
    <b-row v-else>
      <b-col md="3" sm="6" v-for="item in liste" :key="item.id" class="mb-4">
        <b-card class="card-search biblio-card h-100" no-body @click="ouvrir(item)">
          <!-- Vignette : l'image se montre, les autres types affichent leur icône -->
          <div class="biblio-vignette" :class="'bg-light-' + couleur(item.type)">
            <img v-if="item.type === 'image'" :src="$api.link(item.description)" :alt="item.titre" />
            <i v-else class="fas fa-3x" :class="[icone(item.type), 'text-' + couleur(item.type)]"></i>
          </div>
          <div class="p-4">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <span class="h6 mb-0 biblio-titre">{{ item.titre }}</span>
              <b-badge class="ml-2 flex-shrink-0" :variant="'light-' + couleur(item.type)">{{ libelle(item.type) }}</b-badge>
            </div>
            <p v-if="item.type === 'text'" class="text-muted small mb-2 biblio-extrait">{{ extrait(item.description) }}</p>
            <div class="d-flex justify-content-between align-items-center">
              <small class="text-muted">{{ auteur(item.user_id) }}</small>
              <small class="text-muted">{{ afficherDate(item.date_update || item.date_create) }}</small>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Consultation -->
    <b-modal ref="voir" size="lg" hide-footer>
      <template #modal-title>
        <i class="fas mr-2" :class="selected ? icone(selected.type) : ''"></i>
        {{ selected ? selected.titre : '' }}
      </template>
      <div v-if="selected">
        <!-- Une note : on rend le HTML produit par l'éditeur -->
        <div v-if="selected.type === 'text'" class="ql-snow">
          <div class="ql-editor p-0" v-html="selected.description"></div>
        </div>
        <!-- Un document : rendu adapté au type -->
        <div v-else class="text-center">
          <img v-if="selected.type === 'image'" :src="$api.link(selected.description)" class="img-fluid rounded" />
          <video v-else-if="selected.type === 'video'" :src="$api.link(selected.description)" class="w-100 rounded" controls></video>
          <audio v-else-if="selected.type === 'audio'" :src="$api.link(selected.description)" class="w-100" controls></audio>
          <iframe v-else-if="selected.type === 'pdf'" :src="$api.link(selected.description)" class="w-100 rounded border" style="height:70vh"></iframe>
          <div v-else class="py-5">
            <i class="fas fa-4x fa-file d-block mb-3 text-muted"></i>
            <span class="text-muted">Aperçu indisponible pour ce format.</span>
          </div>
        </div>
        <div class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
          <small class="text-muted">
            Ajouté par {{ auteur(selected.user_id) }} le {{ afficherDate(selected.date_create) }}
          </small>
          <b-button-group size="sm">
            <b-button v-if="selected.type !== 'text'" variant="primary" :href="$api.link(selected.description)" target="_blank">
              <i class="fas fa-download mr-1"></i> Télécharger
            </b-button>
            <b-button v-if="$api.acl(36)" variant="light-primary" @click="editer(selected)">
              <i class="fas fa-pen mr-1"></i> Modifier
            </b-button>
          </b-button-group>
        </div>
      </div>
    </b-modal>

    <!-- Création / modification -->
    <b-modal ref="editer" size="lg" hide-footer no-close-on-backdrop>
      <template #modal-title>
        {{ edit && edit.id ? 'Modifier' : 'Ajouter à la bibliothèque' }}
      </template>
      <div v-if="edit">
        <!-- Le choix note / document n'est offert qu'à la création : changer la nature
             d'un élément existant reviendrait à en créer un autre. -->
        <b-form-group v-if="!edit.id" label="Nature">
          <b-button-group class="w-100">
            <b-button :variant="mode === 'text' ? 'primary' : 'light'" @click="basculerMode('text')">
              <i class="fas fa-align-left mr-1"></i> Note
            </b-button>
            <b-button :variant="mode === 'file' ? 'primary' : 'light'" @click="basculerMode('file')">
              <i class="fas fa-paperclip mr-1"></i> Document
            </b-button>
          </b-button-group>
        </b-form-group>

        <b-form-group label="Titre">
          <b-form-input v-model="edit.titre" placeholder="Titre de l'élément"></b-form-input>
        </b-form-group>

        <!-- Note -->
        <b-form-group v-if="mode === 'text'" label="Contenu">
          <vue-editor
            v-model="edit.description"
            :editor-toolbar="editorToolbar"
            placeholder="Rédigez votre note..." />
        </b-form-group>

        <!-- Document -->
        <b-form-group v-else label="Fichier">
          <b-form-file
            @change="chargerFichier"
            :disabled="load_file"
            placeholder="Choisissez votre document"
            drop-placeholder="Déposez le fichier ici..."></b-form-file>
          <div v-if="load_file" class="mt-2 text-primary">
            <i class="fas fa-spinner fa-spin mr-1"></i> Envoi du fichier...
          </div>
          <div v-else-if="edit.description" class="mt-2 d-flex align-items-center">
            <i class="fas mr-2" :class="[icone(edit.type), 'text-' + couleur(edit.type)]"></i>
            <a :href="$api.link(edit.description)" target="_blank" class="text-truncate">{{ edit.description }}</a>
            <b-badge class="ml-2" :variant="'light-' + couleur(edit.type)">{{ libelle(edit.type) }}</b-badge>
          </div>
        </b-form-group>

        <div class="text-right mt-4">
          <b-button variant="secondary" @click="$refs['editer'].hide()">Annuler</b-button>
          <b-button class="ml-2" variant="success" :disabled="!enregistrable" @click="save()">
            <span v-if="load_save === false">Sauvegarder</span>
            <i v-else class="fas fa-spinner fa-spin"></i>
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
// ⚠️ `vue2-quill-editor` porte mal son nom : c'est un republiage de Vue2Editor.
// Il exporte { VueEditor } et déclare <vue-editor> — il n'y a pas de `quillEditor`,
// et la barre d'outils se passe en prop `editor-toolbar`, pas dans un objet `options`.
import { VueEditor } from 'vue2-quill-editor';
// Quill est une dépendance du paquet ; on importe ses feuilles explicitement car la
// modale de consultation rend du HTML Quill (classes ql-snow / ql-editor) sans que
// l'éditeur soit monté.
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';

// Extensions reconnues, par type. Tout ce qui n'est pas listé tombe dans « autre ».
const EXTENSIONS = {
  pdf: ['pdf'],
  image: ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'],
  video: ['mp4', 'mkv', 'mov', 'avi', 'webm', 'm4v'],
  audio: ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac']
};

const LIBELLES = {
  text: 'Note',
  pdf: 'PDF',
  image: 'Image',
  video: 'Vidéo',
  audio: 'Audio',
  autre: 'Fichier'
};

const ICONES = {
  text: 'fa-align-left',
  pdf: 'fa-file-pdf',
  image: 'fa-file-image',
  video: 'fa-file-video',
  audio: 'fa-file-audio',
  autre: 'fa-file'
};

const COULEURS = {
  text: 'primary',
  pdf: 'danger',
  image: 'success',
  video: 'warning',
  audio: 'info',
  autre: 'secondary'
};

export default {
  name: 'Bibliotheque',
  components: { VueEditor },
  data: () => ({
    items: [],
    load_liste: false,
    load_new: false,
    load_save: false,
    load_file: false,
    search: '',
    filtre_type: null,
    selected: null,
    edit: null,
    mode: 'text',
    editorToolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: [1, 2, 3, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ]
  }),
  computed: {
    // La recherche et le filtre sont faits côté client : la liste tient en mémoire
    // et l'API ne propose pas de filtrage.
    liste() {
      var res = this.items;
      if (this.filtre_type !== null) {
        res = res.filter(i => this.typeOuAutre(i.type) === this.filtre_type);
      }
      var q = this.search.toLowerCase().trim();
      if (q !== '') {
        res = res.filter(i => {
          var titre = (i.titre || '').toLowerCase();
          // Sur une note, on cherche dans le texte, pas dans les balises de l'éditeur.
          var contenu = i.type === 'text' ? this.extrait(i.description).toLowerCase() : '';
          return titre.indexOf(q) !== -1 || contenu.indexOf(q) !== -1;
        });
      }
      return res.slice().sort((a, b) => {
        return (b.date_update || b.date_create || 0) > (a.date_update || a.date_create || 0) ? 1 : -1;
      });
    },
    filtres() {
      var res = [{ titre: 'Tout', value: null, total: this.items.length }];
      Object.keys(LIBELLES).forEach(t => {
        var total = this.items.filter(i => this.typeOuAutre(i.type) === t).length;
        if (total > 0) res.push({ titre: LIBELLES[t], value: t, total: total });
      });
      return res;
    },
    // Un titre est toujours exigé ; une note veut du contenu, un document veut son fichier.
    enregistrable() {
      if (this.edit === null || this.load_file) return false;
      if (!this.edit.titre || this.edit.titre.trim() === '') return false;
      return !!this.edit.description;
    }
  },
  methods: {
    typeOuAutre(type) {
      return LIBELLES[type] === undefined ? 'autre' : type;
    },
    libelle(type) {
      return LIBELLES[this.typeOuAutre(type)];
    },
    icone(type) {
      return ICONES[this.typeOuAutre(type)];
    },
    couleur(type) {
      return COULEURS[this.typeOuAutre(type)];
    },
    auteur(user_id) {
      var u = this.$api.getParam('User', user_id);
      return u.titre === undefined ? 'Utilisateur inconnu' : u.titre;
    },
    // L'API renvoie tantôt un timestamp, tantôt une date déjà formatée : on gère les deux.
    afficherDate(valeur) {
      if (valeur === null || valeur === undefined || valeur === '') return '';
      if (isNaN(valeur) === false) return this.$api.timestampToDate(Number(valeur), false);
      return this.$api.moment(valeur).format('DD/MM/YYYY');
    },
    // Texte brut d'une note : sert à l'aperçu de la carte comme à la recherche.
    extrait(html) {
      if (!html) return '';
      var texte = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
      return texte.length > 120 ? texte.substring(0, 120) + '...' : texte;
    },
    typeDepuisNom(nom) {
      var ext = (nom.split('.').pop() || '').toLowerCase();
      var res = 'autre';
      Object.keys(EXTENSIONS).forEach(t => {
        if (EXTENSIONS[t].indexOf(ext) !== -1) res = t;
      });
      return res;
    },
    init() {
      this.load_liste = true;
      this.$api.ajax('/bibliotheque/liste', null, res => {
        if (res.status === true) {
          this.items = res.data;
        }
        this.load_liste = false;
      });
    },
    nouveau() {
      if (this.load_new) return false;
      this.load_new = true;
      this.$api.ajax('/bibliotheque/new', null, res => {
        if (res.status === true) {
          this.edit = res.data;
          this.mode = 'text';
          this.edit.type = 'text';
          this.edit.description = null;
          this.$refs['editer'].show();
        }
        this.load_new = false;
      });
    },
    editer(item) {
      // Copie : tant que l'API n'a pas confirmé, la liste affichée ne bouge pas.
      this.edit = JSON.parse(JSON.stringify(item));
      this.mode = item.type === 'text' ? 'text' : 'file';
      this.$refs['voir'].hide();
      this.$refs['editer'].show();
    },
    basculerMode(mode) {
      if (this.mode === mode) return false;
      this.mode = mode;
      // Le contenu d'une note et le chemin d'un fichier partagent le même champ :
      // on repart de zéro pour ne pas laisser l'un dans l'autre.
      this.edit.description = null;
      this.edit.type = mode === 'text' ? 'text' : null;
    },
    chargerFichier(e) {
      var files = e.target.files;
      if (files.length === 0) return false;
      const fichier = files[0];
      const reader = new FileReader();
      this.load_file = true;
      reader.onload = ev => {
        var params = {
          file: ev.target.result,
          file_name: fichier.name
        };
        this.$api.ajax('/bibliotheque/upload', params, res => {
          if (res.status === true) {
            // L'API renvoie le chemin du fichier : c'est lui qu'on stocke en description.
            this.edit.description = res.data;
            this.edit.type = this.typeDepuisNom(fichier.name);
          }
          this.load_file = false;
        });
      };
      reader.readAsDataURL(fichier);
    },
    save() {
      if (this.load_save || !this.enregistrable) return false;
      this.load_save = true;
      this.$api.ajax('/bibliotheque/create', this.edit, res => {
        if (res.status === true) {
          this.$bvToast.toast('Élément sauvegardé', {
            title: 'Bibliothèque',
            variant: 'success',
            solid: true
          });
          this.$refs['editer'].hide();
          this.edit = null;
          this.selected = null;
          this.init();
        }
        this.load_save = false;
      });
    },
    ouvrir(item) {
      this.selected = item;
      this.$refs['voir'].show();
    }
  },
  mounted() {
    this.init();
  }
};
</script>

<style lang="scss" scoped>
.biblio-search {
  max-width: 340px;
}

.biblio-card {
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12) !important;
  }
}

.biblio-vignette {
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.biblio-titre {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.biblio-extrait {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.4em;
}

.biblio-filtres {
  display: flex;
  flex-wrap: wrap;
}
</style>
